import { AxiosResponse } from 'axios'
import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../utils/api'
import { getPricesMode, getCountryCode } from '../config'
import { DialogActions } from '../dialog'
import { withLoading, withLoadingParametrized } from '../loading'
import { OrderActions } from '../order'
import {
  mapOrderDataFromResponse,
  mapOrderResponseItemsToOrderItems,
  OrderResponse,
  resetOrderMetadata
} from '../order/sagas'
import { isUserLoggedIn } from '../user/selectors'

import { CheckoutActions } from './actions'
import { CountriesResponse, PaymentLinkResponse, PaymentOptions, PaymentOptionsRes, StatesResponse } from './types'

export function* loadCheckoutOrder({ payload }: ReturnType<typeof CheckoutActions.loadCheckoutOrder>) {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return
  const { orderId } = payload

  try {
    const { data }: AxiosResponse<OrderResponse> = yield call(apiCall, {
      url: '/api/ordering/order',
      method: 'get',
      params: {
        orderId
      }
    })
    const orderData = mapOrderDataFromResponse(data)

    if (orderData) {
      yield put(CheckoutActions.setCheckoutOrderData({ order: orderData }))
    } else {
      yield call(resetOrderMetadata, {})
    }

    yield put(
      CheckoutActions.setCheckoutOrderItems({
        items: mapOrderResponseItemsToOrderItems(data?.data?.order?.customerRequests || [])
      })
    )

    yield put(CheckoutActions.setPayPerClick({ payPerClick: data?.data?.payPerClick }))
  } catch (e) {
    yield call(resetOrderMetadata, e)
  }
}

export function* placeOrder({ payload }: ReturnType<typeof CheckoutActions.placeOrder>) {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return

  const { orderId: orderId, resetCart, customerReference, paymentMethod, cancelUrl, returnUrl } = payload

  try {
    const showTaxes: boolean = yield select(getPricesMode)
    const { data }: AxiosResponse<PaymentLinkResponse> = yield call(apiCall, {
      url: `/api/ordering/order/${orderId}/paymentLink`,
      method: 'post',
      data: {
        paymentMethod: paymentMethod || 'None',
        customerReference,
        note: 'Payment note',
        returnUrl,
        cancelUrl,
        excludeTax: !showTaxes
      }
    })

    const { successful, customerRequestIds, paymentLink } = data.data

    if (!successful) throw new Error('Payment failed!')

    if (paymentLink) {
      window.location.href = paymentLink
    } else {
      yield put(CheckoutActions.orderPlaced({ customerRequestIds }))
      if (resetCart) yield put(OrderActions.reset())
    }
  } catch (e) {
    yield put(
      DialogActions.showInfoDialog({
        i18nText: 'checkout:PaymentLinkError',
        i18nTitle: 'core:Error',
        namespaces: ['checkout']
      })
    )
    yield put(CheckoutActions.orderFailed({ error: e }))
  }
}

export function* loadCountries() {
  const loggedIn: boolean = yield select(isUserLoggedIn)
  if (!loggedIn) return
  const domainCode: string = yield select(getCountryCode)

  const { data }: AxiosResponse<CountriesResponse> = yield call(apiCall, {
    url: `/api/countries/${domainCode}`,
    method: 'get'
  })

  yield put(CheckoutActions.setCountries(data))
}

export function* loadStates({ payload }: ReturnType<typeof CheckoutActions.loadStates>) {
  const loggedId: boolean = yield select(isUserLoggedIn)
  if (!loggedId) return

  const { countryCode } = payload

  const { data }: AxiosResponse<StatesResponse> = yield call(apiCall, {
    url: `/api/countries/${countryCode}/states`,
    method: 'get'
  })

  yield put(CheckoutActions.setStates(data))
}

export const mapPaymentOptionResponseToPaymentOptions = (res: PaymentOptionsRes): PaymentOptions =>
  _.map(res, x => ({
    id: x.id,
    images: _.map(x.images, x => ({ altText: x.altText, url: x.url })),
    backendIdentifier: x.backendIdentifier,
    text: x.text
  }))

export function* loadPaymentOptions({ payload }: ReturnType<typeof CheckoutActions.loadPaymentOptions>) {
  const { paymentOptionCode } = payload

  const { data }: AxiosResponse<PaymentOptionsRes> = yield call(apiCall, {
    url: '/api/content/GetPaymentMethods',
    params: {
      paymentOption: paymentOptionCode
    },
    umbracoCall: true
  })

  yield put(
    CheckoutActions.setPaymentOptions({
      paymentOptions: mapPaymentOptionResponseToPaymentOptions(data)
    })
  )
}

/* istanbul ignore next */
export function* checkoutSagas() {
  yield all([
    takeLatest(getType(CheckoutActions.loadCheckoutOrder), withLoading('checkout', loadCheckoutOrder)),
    takeLatest(
      getType(CheckoutActions.placeOrder),
      withLoadingParametrized('checkout', placeOrder, _.constant('payment'))
    ),
    takeLatest(getType(CheckoutActions.loadCountries), loadCountries),
    takeLatest(getType(CheckoutActions.loadStates), loadStates),
    takeLatest(getType(CheckoutActions.loadPaymentOptions), loadPaymentOptions)
  ])
}
